(() => {
  const STORAGE_KEY = 'teveo-wishlist-variant-ids';
  const BUTTON_SELECTOR = '[data-wishlist-button], .teveo-wishlist-button';
  const ACTIVE_CLASS = 'is-active';
  const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
  const WINDOW_NAME_PREFIX = 'teveoWishlist:';
  const CUSTOMER_ID = String(window.teveoWishlistCustomerId || '');
  const SYNCED_CUSTOMER_KEY = 'teveo-wishlist-customer-id';
  const PENDING_GUEST_KEY = `teveo-wishlist-pending-guest:${CUSTOMER_ID}`;
  const WISHLIST_LIMIT = 256;
  // Tells the app that this theme shows both wishlist fields. Without it, the
  // app reads and writes only the first field, for the theme before it.
  const STORAGE_VERSION = 2;
  const LIMIT_ERROR_CODE = 'WISHLIST_LIMIT_REACHED';
  let wishlistVariantIds = null;
  let resolveAccountVariantIds;
  const accountVariantIdsReady = new Promise((resolve) => {
    resolveAccountVariantIds = resolve;
  });

  // The wishlist page waits for this to render items Liquid could not.
  window.teveoWishlist = {
    accountVariantIds: accountVariantIdsReady
  };

  const collectVariantIds = (item) => {
    if (Array.isArray(item)) {
      return item.flatMap(collectVariantIds);
    }

    if (item && typeof item === 'object') {
      const nestedItems = [
        ...(Array.isArray(item.all) ? item.all : []),
        ...(Array.isArray(item.items) ? item.items : []),
        ...(Array.isArray(item.products) ? item.products : [])
      ];

      return [
        item.epi,
        item.variantId,
        item.variant_id,
        item.variant?.id,
        String(item.id || '').match(/^gid:\/\/shopify\/ProductVariant\/\d+$/) ? item.id : null,
        ...nestedItems.flatMap(collectVariantIds)
      ];
    }

    return String(item || '').split(',');
  };

  const normalizeVariantIds = (value) => {
    return collectVariantIds(value)
      .map((id) => String(id || '').trim())
      .map((id) => id.match(/^gid:\/\/shopify\/ProductVariant\/(\d+)$/)?.[1] || id)
      .filter((id) => /^\d+$/.test(id))
      .filter((id, index, list) => list.indexOf(id) === index);
  };

  const parseStoredWishlistValue = (value) => {
    if (!value) {
      return [];
    }

    try {
      return normalizeVariantIds(JSON.parse(value));
    } catch {
      return normalizeVariantIds(value);
    }
  };

  const getCookieValue = (key) => {
    const encodedKey = `${encodeURIComponent(key)}=`;
    const value = document.cookie
      .split(';')
      .map((item) => item.trim())
      .find((item) => item.startsWith(encodedKey))
      ?.slice(encodedKey.length) || '';
    return value ? decodeURIComponent(value) : '';
  };

  const setCookieValue = (key, value) => {
    document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
  };

  const removeCookieValue = (key) => {
    document.cookie = `${encodeURIComponent(key)}=; path=/; max-age=0; SameSite=Lax`;
  };

  const getStoredValue = (key) => {
    try {
      return window.localStorage?.getItem(key) || getCookieValue(key) || getWindowNameValue();
    } catch {
      return getCookieValue(key) || getWindowNameValue();
    }
  };

  const getWindowNameValue = () => {
    return window.name.startsWith(WINDOW_NAME_PREFIX)
      ? window.name.slice(WINDOW_NAME_PREFIX.length)
      : '';
  };

  const setWindowNameValue = (value) => {
    window.name = value ? `${WINDOW_NAME_PREFIX}${value}` : '';
  };

  const getStoredVariantIds = (key) => {
    return parseStoredWishlistValue(getStoredValue(key));
  };

  const getQueryVariantIds = () => {
    const searchParams = new URLSearchParams(window.location.search);
    return normalizeVariantIds([
      ...searchParams.getAll('variantIds'),
      ...searchParams.getAll('variantId'),
      searchParams.get('variants')
    ]);
  };

  const getWishlist = () => {
    if (Array.isArray(wishlistVariantIds)) {
      return [...wishlistVariantIds];
    }

    const storedVariantIds = getStoredVariantIds(STORAGE_KEY);
    if (storedVariantIds.length > 0) {
      wishlistVariantIds = storedVariantIds;
      return [...wishlistVariantIds];
    }

    const queryVariantIds = getQueryVariantIds();
    if (queryVariantIds.length > 0) {
      wishlistVariantIds = queryVariantIds;
      return [...wishlistVariantIds];
    }

    return [];
  };

  const setWishlist = (variantIds) => {
    const nextVariantIds = normalizeVariantIds(variantIds);
    const value = JSON.stringify(nextVariantIds);
    wishlistVariantIds = nextVariantIds;

    try {
      if (nextVariantIds.length > 0) {
        window.localStorage?.setItem(STORAGE_KEY, value);
      } else {
        window.localStorage?.removeItem(STORAGE_KEY);
      }
    } catch {}

    if (nextVariantIds.length > 0) {
      setCookieValue(STORAGE_KEY, value);
      setWindowNameValue(value);
    } else {
      removeCookieValue(STORAGE_KEY);
      setWindowNameValue('');
    }

    window.dispatchEvent(new CustomEvent('teveo:wishlist:updated', {
      detail: {variantIds: nextVariantIds}
    }));

    return nextVariantIds;
  };

  const isWishlistPage = () => {
    return window.location.pathname.endsWith('/pages/wishlist');
  };

  const getButtonVariantId = (button) => {
    if (button.closest('.wishlist-card')) {
      return normalizeVariantIds(button.dataset.variantId)[0];
    }

    const productForm = button.closest('form[action*="/cart/add"]');
    const selectedVariantInput = productForm?.querySelector('[name="id"]');
    return normalizeVariantIds(selectedVariantInput?.value || button.dataset.variantId)[0];
  };

  // Returns the server's list only when every entry is a variant ID.
  const getPayloadVariantIds = (payload) => {
    const variantIds = payload?.variantIds;
    if (!Array.isArray(variantIds) || !variantIds.every((id) => normalizeVariantIds([id]).length === 1)) {
      return null;
    }

    return normalizeVariantIds(variantIds);
  };

  // Guests keep their wishlist in the browser only, so only logged-in
  // customers call the app proxy. Resolves to {ok, payload}. A save counts
  // only when the app confirms it and returns the saved list.
  const saveWishlistOperation = async (operation, variantIds) => {
    try {
      const response = await fetch('/apps/wishlist', {
        method: 'POST',
        headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
        body: JSON.stringify({
          operation,
          variantIds: normalizeVariantIds(variantIds),
          storageVersion: STORAGE_VERSION
        })
      });
      const payload = await response.json().catch(() => null);

      return {ok: response.ok && payload?.ok === true && getPayloadVariantIds(payload) !== null, payload};
    } catch (error) {
      console.error('Error syncing wishlist:', error);
      return {ok: false, payload: null};
    }
  };

  const fetchAccountVariantIds = async () => {
    const response = await fetch(`/apps/wishlist?fields=ids&storageVersion=${STORAGE_VERSION}`, {headers: {'Accept': 'application/json'}});
    const payload = await response.json().catch(() => null);

    if (!response.ok || !Array.isArray(payload?.variantIds)) {
      throw new Error(`Wishlist request returned ${response.status}`);
    }

    return normalizeVariantIds(payload.variantIds);
  };

  const getToastMessage = (key, fallback) => {
    return document.querySelector('toast-notification')?.dataset[key] || fallback;
  };

  const showWishlistError = (payload) => {
    if (typeof window.showToast !== 'function') {
      return;
    }

    const message = payload?.code === LIMIT_ERROR_CODE
      ? getToastMessage('wishlistLimitMessage', `Your wishlist is full. Remove an item to add another (maximum ${WISHLIST_LIMIT}).`)
      : getToastMessage('wishlistErrorMessage', 'Your wishlist could not be saved. Please try again.');

    window.showToast({
      message,
      duration: 5000,
      anchorSelector: '[data-wishlist-header]'
    });
  };

  const getPendingGuestVariantIds = () => {
    try {
      return CUSTOMER_ID ? parseStoredWishlistValue(window.localStorage?.getItem(PENDING_GUEST_KEY)) : [];
    } catch {
      return [];
    }
  };

  const setPendingGuestVariantIds = (variantIds) => {
    try {
      if (variantIds.length > 0) {
        window.localStorage?.setItem(PENDING_GUEST_KEY, JSON.stringify(variantIds));
      } else {
        window.localStorage?.removeItem(PENDING_GUEST_KEY);
      }
    } catch {}
  };

  const getLocalePrefix = () => {
    const rootUrl = window.routes?.root_url || '';
    if (rootUrl && rootUrl !== '/') {
      return rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
    }

    const localePath = window.location.pathname.match(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i)?.[0];
    return localePath ? `${localePath}/` : '/';
  };

  const getWishlistHref = (variantIds = getWishlist()) => {
    const wishlistUrl = new URL(`${getLocalePrefix()}pages/wishlist`, window.location.origin);
    const normalizedVariantIds = normalizeVariantIds(variantIds);
    if (normalizedVariantIds.length > 0) {
      wishlistUrl.searchParams.set('variants', normalizedVariantIds.join(','));
    }
    return `${wishlistUrl.pathname}${wishlistUrl.search}`;
  };

  const syncCurrentWishlistUrl = (variantIds = getWishlist()) => {
    if (!isWishlistPage()) {
      return;
    }

    const nextHref = getWishlistHref(variantIds);
    const currentHref = `${window.location.pathname}${window.location.search}`;
    if (currentHref !== nextHref) {
      window.history.replaceState({}, '', nextHref);
    }
  };

  const updateHeaderCount = (variantIds = getWishlist()) => {
    const header = document.querySelector('[data-wishlist-header], .header__icon--wishlist');
    const bubble = header?.querySelector('.wishlistbuble');
    const countElements = bubble?.querySelectorAll('span');

    if (!header || !bubble || !countElements) {
      return;
    }

    header.href = getWishlistHref(variantIds);

    const count = variantIds.length;
    header.classList.toggle(ACTIVE_CLASS, count > 0);
    bubble.style.display = count > 0 ? 'block' : 'none';
    countElements.forEach((element) => {
      element.textContent = count;
      element.classList.toggle('visually-hidden', count === 0);
    });
  };

  const updateButtons = () => {
    const variantIds = getWishlist();
    document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
      const variantId = getButtonVariantId(button);
      const isActive = variantIds.includes(variantId);
      button.classList.toggle(ACTIVE_CLASS, isActive);
      button.querySelector('.heart-icon')?.classList.toggle(ACTIVE_CLASS, isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    updateHeaderCount(variantIds);
  };

  const getSyncedCustomerId = () => {
    try {
      return window.localStorage?.getItem(SYNCED_CUSTOMER_KEY) || '';
    } catch {
      return '';
    }
  };

  const setSyncedCustomerId = (customerId) => {
    try {
      if (customerId) {
        window.localStorage?.setItem(SYNCED_CUSTOMER_KEY, customerId);
      } else {
        window.localStorage?.removeItem(SYNCED_CUSTOMER_KEY);
      }
    } catch {}
  };

  const applyWishlist = (variantIds) => {
    const nextVariantIds = setWishlist(variantIds);
    updateButtons();
    syncCurrentWishlistUrl(nextVariantIds);
    return nextVariantIds;
  };

  // The first page load sync and every save run one at a time, so responses
  // arrive in the order the server handled them. The browser shows the last
  // list the server confirmed, with the saves still waiting applied on top.
  // A failed save then drops only its own change.
  let saveQueue = Promise.resolve();
  let confirmedVariantIds = null;
  const pendingOperations = [];

  const enqueue = (task) => {
    const result = saveQueue.then(task);
    saveQueue = result.catch(() => {});
    return result;
  };

  const applyOperation = (variantIds, {operation, variantIds: changedIds}) => {
    return operation === 'add'
      ? [...variantIds, ...changedIds.filter((id) => !variantIds.includes(id))]
      : variantIds.filter((id) => !changedIds.includes(id));
  };

  const showConfirmedWishlist = () => {
    return applyWishlist(pendingOperations.reduce(applyOperation, confirmedVariantIds || []));
  };

  const addPendingOperation = (operation, variantIds) => {
    const pending = {operation, variantIds: normalizeVariantIds(variantIds)};
    pendingOperations.push(pending);
    return pending;
  };

  // Call only from inside the queue.
  const runSave = async (pending) => {
    const result = await saveWishlistOperation(pending.operation, pending.variantIds);
    pendingOperations.splice(pendingOperations.indexOf(pending), 1);

    // A rejected save may still report the account's current list.
    const payloadVariantIds = getPayloadVariantIds(result.payload);
    if (result.ok || (result.payload?.ok === false && payloadVariantIds)) {
      confirmedVariantIds = payloadVariantIds;
    }
    showConfirmedWishlist();

    return result;
  };

  // The customer metafields are the source of truth. The mobile app and the
  // Shopify admin write them too, so the browser copy is replaced on every page
  // load. Only the first page load after login carries guest items over; guest
  // items that could not be saved are kept aside and tried again on later loads.
  // The wishlist page gets the saved list without items removed meanwhile, so
  // it can add cards for merged guest items without a reload.
  const syncLoggedInWishlist = async () => {
    const syncedCustomerId = getSyncedCustomerId();

    if (!CUSTOMER_ID) {
      resolveAccountVariantIds(null);
      if (syncedCustomerId) {
        setSyncedCustomerId('');
        applyWishlist([]);
      }
      return;
    }

    if (!Array.isArray(window.teveoWishlistAccountVariantIds)) {
      confirmedVariantIds = getWishlist();
      resolveAccountVariantIds(null);
      return;
    }

    // Read before waiting, so a toggle during the wait is not taken for a
    // guest item.
    const browserVariantIds = getWishlist();
    let accountVariantIds = normalizeVariantIds(window.teveoWishlistAccountVariantIds);
    if (window.teveoWishlistAccountComplete === false) {
      accountVariantIds = await fetchAccountVariantIds().catch((error) => {
        console.error('Error loading wishlist:', error);
        return accountVariantIds;
      });
    }

    const newGuestVariantIds = syncedCustomerId === CUSTOMER_ID
      ? []
      : browserVariantIds.filter((id) => !accountVariantIds.includes(id));
    const guestVariantIds = normalizeVariantIds([...getPendingGuestVariantIds(), ...newGuestVariantIds])
      .filter((id) => !accountVariantIds.includes(id));

    setSyncedCustomerId(CUSTOMER_ID);
    confirmedVariantIds = accountVariantIds;

    const resolveSavedVariantIds = () => {
      const shownVariantIds = showConfirmedWishlist();
      resolveAccountVariantIds(confirmedVariantIds.filter((id) => shownVariantIds.includes(id)));
    };

    if (guestVariantIds.length === 0) {
      setPendingGuestVariantIds([]);
      resolveSavedVariantIds();
      return;
    }

    setPendingGuestVariantIds(guestVariantIds);
    const pending = addPendingOperation('add', guestVariantIds);
    showConfirmedWishlist();

    const {ok, payload} = await runSave(pending);
    resolveSavedVariantIds();

    if (!ok) {
      if (newGuestVariantIds.length > 0) {
        showWishlistError(payload);
      }
      return;
    }

    setPendingGuestVariantIds([]);
  };

  const getToastImage = (button) => {
    const card = button.closest('.card-wrapper, .wishlist-card, .lg-modaltemp');
    const cardImage = card?.querySelector('img');
    const productImage = button.closest('.product__info-wrapper, .product__info-container')
      ? document.querySelector('media-gallery .product__media-item.is-active img') || document.querySelector('media-gallery img')
      : null;
    const image = productImage || cardImage;

    return {
      url: image?.currentSrc || image?.src || image?.dataset?.src || '',
      alt: image?.alt || ''
    };
  };

  const showWishlistAddedToast = (button) => {
    if (typeof window.showToast !== 'function') {
      return;
    }

    const image = getToastImage(button);
    const wishlistMessage = document.querySelector('toast-notification')?.dataset.wishlistMessage || 'Added to your wishlist';
    window.showToast({
      message: wishlistMessage,
      duration: 3200,
      imageUrl: image.url,
      imageAlt: image.alt,
      variant: 'wishlist',
      anchorSelector: '[data-wishlist-header]',
      actionUrl: getWishlistHref()
    });
  };

  const toggleWishlist = async (button) => {
    const variantId = getButtonVariantId(button);
    if (!variantId) {
      return;
    }

    const currentVariantIds = getWishlist();
    const isActive = currentVariantIds.includes(variantId);

    if (!isActive && currentVariantIds.length >= WISHLIST_LIMIT) {
      showWishlistError({code: LIMIT_ERROR_CODE});
      return;
    }

    const nextVariantIds = isActive
      ? currentVariantIds.filter((id) => id !== variantId)
      : [...currentVariantIds, variantId];

    applyWishlist(nextVariantIds);
    if (!isActive && !CUSTOMER_ID) {
      showWishlistAddedToast(button);
    }

    button.dispatchEvent(new CustomEvent('teveo:wishlist:toggled', {
      bubbles: true,
      detail: {
        isActive: !isActive,
        variantId,
        variantIds: nextVariantIds,
      },
    }));

    if (!CUSTOMER_ID) {
      return;
    }

    const operation = isActive ? 'remove' : 'add';
    const pending = addPendingOperation(operation, [variantId]);
    const {ok, payload} = await enqueue(() => runSave(pending));

    if (ok) {
      if (!isActive) {
        showWishlistAddedToast(button);
      }
      return;
    }

    showWishlistError(payload);

    document.dispatchEvent(new CustomEvent('teveo:wishlist:save-failed', {
      detail: {operation, variantId}
    }));
  };

  let initialized = false;

  const initWishlist = () => {
    if (initialized) {
      return;
    }

    initialized = true;
    updateButtons();
    enqueue(syncLoggedInWishlist).catch((error) => {
      console.error('Error syncing wishlist:', error);
      resolveAccountVariantIds(null);
    });

    document.addEventListener('click', (event) => {
      const button = event.target.closest(BUTTON_SELECTOR);
      if (!button) {
        return;
      }

      event.preventDefault();
      toggleWishlist(button);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlist);
  } else {
    initWishlist();
  }

  window.setTimeout(() => {
    updateButtons();
  }, 1000);
  document.addEventListener('change', (event) => {
    if (event.target.matches('[name="id"]')) {
      window.setTimeout(updateButtons, 0);
    }
  });
  document.addEventListener('theme:loading:end', () => window.setTimeout(updateButtons, 100));
  document.addEventListener('teveo:product:changed', updateButtons);
  window.addEventListener('teveo:wishlist:updated', (event) => {
    updateButtons();
    updateHeaderCount(event.detail?.variantIds);
    syncCurrentWishlistUrl(event.detail?.variantIds);
  });
})();
